import { useEffect, useState } from 'react';
import { Bookmark, Trash2 } from 'lucide-react';
import type { CustomQuery, SavedCustomMetric } from '@contracts';
import { deleteMetricDef, fetchSavedMetrics, saveMetricDef } from '@/services/customMetricsService';

/**
 * Biblioteca de plantillas de métricas personalizadas: cargar una guardada para
 * prellenar el builder, o guardar la actual con nombre. El widget sigue llevando
 * su propia copia inline (la plantilla es solo punto de partida reutilizable).
 */
export function SavedMetricsBar({
  current,
  onLoad,
}: {
  current: CustomQuery | null;
  onLoad: (q: CustomQuery) => void;
}) {
  const [list, setList] = useState<SavedCustomMetric[]>([]);
  const [sel, setSel] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetchSavedMetrics().then(setList).catch(() => setList([]));
  }, []);

  const load = (id: string) => {
    setSel(id);
    const m = list.find((x) => x.id === id);
    if (m) onLoad(m.query);
  };

  const save = async () => {
    if (!current || !name.trim()) return;
    setBusy(true);
    try {
      const def: SavedCustomMetric = { id: `sm-${Date.now()}`, name: name.trim(), query: current };
      setList(await saveMetricDef(def));
      setName('');
    } catch {
      /* el botón vuelve a habilitarse; sin toast por ahora */
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!sel) return;
    setBusy(true);
    try {
      setList(await deleteMetricDef(sel));
      setSel('');
    } catch {
      /* ignore */
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 p-3">
      <div className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-500">
        <Bookmark className="h-3.5 w-3.5" /> Métricas guardadas
      </div>
      {list.length > 0 && (
        <div className="mb-2 flex gap-2">
          <select
            value={sel}
            onChange={(e) => load(e.target.value)}
            className="flex-1 rounded-lg border border-slate-300 px-2.5 py-2 text-sm outline-none focus:border-red-600"
          >
            <option value="">Cargar plantilla…</option>
            {list.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
          {sel && (
            <button
              onClick={remove}
              disabled={busy}
              title="Eliminar plantilla"
              className="rounded-lg border border-slate-300 px-2.5 text-slate-400 hover:border-red-300 hover:text-red-600 disabled:opacity-40"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      )}
      <div className="flex gap-2">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Guardar la métrica actual como…"
          className="flex-1 rounded-lg border border-slate-300 px-2.5 py-2 text-sm outline-none focus:border-red-600"
        />
        <button
          onClick={save}
          disabled={!current || !name.trim() || busy}
          className="rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-40"
        >
          Guardar
        </button>
      </div>
    </div>
  );
}
